import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Check, X, ClipboardCheck } from "lucide-react";
import { listAllCitySubmissions, reviewCitySubmission } from "@/lib/city-teams.functions";

export const Route = createFileRoute("/_authenticated/admin/city-submissions")({
  head: () => ({ meta: [
    { title: "City Submissions — Admin — What Finland" },
    { name: "robots", content: "noindex,nofollow" },
  ] }),
  component: AdminCitySubmissions,
});

function AdminCitySubmissions() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>("submitted");
  const [note, setNote] = useState<Record<string, string>>({});

  const q = useQuery({
    queryKey: ["admin","city-submissions", statusFilter],
    queryFn: () => listAllCitySubmissions({ data: { status: statusFilter || undefined } }),
  });

  const mut = useMutation({
    mutationFn: (args: { id: string; action: "approve"|"reject"; adminNote?: string }) =>
      reviewCitySubmission({ data: args }),
    onSuccess: (_r, args) => {
      toast.success(args.action === "approve" ? "Approved and applied." : "Rejected.");
      qc.invalidateQueries({ queryKey: ["admin","city-submissions"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <ClipboardCheck className="h-5 w-5 text-slate-900" />
          <h1 className="text-lg font-semibold text-slate-900">City Submissions</h1>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="ml-auto rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm">
            <option value="submitted">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="cancelled">Cancelled</option>
            <option value="">All</option>
          </select>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        {q.isLoading ? <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div> :
         q.error ? <p className="text-sm text-rose-600">Failed to load.</p> :
         !q.data?.length ? <p className="text-sm text-slate-500 text-center py-8">No submissions.</p> : (
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          q.data.map((s: any) => (
            <div key={s.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-sm font-semibold text-slate-900">{s.cities?.name ?? "—"}</span>
                <span className="text-xs text-slate-500">·</span>
                <span className="text-sm capitalize text-slate-700">{s.kind}</span>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${
                  s.status === "approved" ? "bg-emerald-100 text-emerald-700" :
                  s.status === "rejected" ? "bg-rose-100 text-rose-700" :
                  s.status === "submitted" ? "bg-amber-100 text-amber-700" :
                  "bg-slate-100 text-slate-600"
                }`}>{s.status}</span>
                <span className="ml-auto text-xs text-slate-500">{new Date(s.created_at).toLocaleString()}</span>
              </div>
              {s.editor_note && <p className="text-xs text-slate-600 mb-3"><strong>Editor note:</strong> {s.editor_note}</p>}

              <SubmissionDiff kind={s.kind} current={s.cities} changes={s.changes} />

              {s.status === "submitted" && (
                <div className="mt-4 space-y-2">
                  <textarea placeholder="Optional note to editor…" rows={2}
                    value={note[s.id] ?? ""} onChange={(e) => setNote({ ...note, [s.id]: e.target.value })}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm" />
                  <div className="flex justify-end gap-2">
                    <button disabled={mut.isPending} onClick={() => mut.mutate({ id: s.id, action: "reject", adminNote: note[s.id] })}
                      className="inline-flex items-center gap-1 text-sm px-3 py-2 rounded-xl bg-rose-100 text-rose-700 hover:bg-rose-200">
                      <X className="h-4 w-4" /> Reject
                    </button>
                    <button disabled={mut.isPending} onClick={() => mut.mutate({ id: s.id, action: "approve", adminNote: note[s.id] })}
                      className="inline-flex items-center gap-1 text-sm px-3 py-2 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700">
                      <Check className="h-4 w-4" /> Approve & Apply
                    </button>
                  </div>
                </div>
              )}
              {s.admin_note && s.status !== "submitted" && (
                <p className="mt-3 text-xs text-slate-600"><strong>Admin note:</strong> {s.admin_note}</p>
              )}
            </div>
          ))
        )}
      </main>
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function SubmissionDiff({ kind, current, changes }: { kind: string; current: any; changes: any }) {
  if (kind === "profile") {
    const rows = Object.entries(changes || {});
    if (!rows.length) return <p className="text-xs text-slate-500">No fields changed.</p>;
    return (
      <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 text-sm overflow-x-auto">
        <table className="w-full min-w-[420px]">
          <thead className="text-xs text-slate-500 text-left"><tr><th className="pb-1">Field</th><th>Current</th><th>New</th></tr></thead>
          <tbody>
            {rows.map(([k, v]) => (
              <tr key={k} className="border-t border-slate-100">
                <td className="py-1 pr-2 font-medium capitalize">{k}</td>
                <td className="text-slate-500 pr-2 truncate max-w-xs">{String(current?.[k] ?? "—")}</td>
                <td className="text-slate-900 truncate max-w-xs">{String(v ?? "—")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    );
  }
  if (kind === "gallery") {
    const imgs = (changes?.images ?? []) as Array<{ url: string }>;
    return (
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        {imgs.map((im, i) => (
          <div key={i} className="aspect-square rounded-lg overflow-hidden bg-slate-100 relative">
            <img src={im.url} alt="" className="w-full h-full object-cover" />
            {i === 0 && <span className="absolute bottom-1 left-1 text-[9px] font-bold bg-slate-900 text-white px-1.5 py-0.5 rounded">Hero</span>}
          </div>
        ))}
      </div>
    );
  }
  // POI
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 text-sm space-y-1">
      <p><strong>Name:</strong> {changes?.name}</p>
      {changes?.category && <p><strong>Category:</strong> {changes.category}</p>}
      {changes?.address && <p><strong>Address:</strong> {changes.address}</p>}
      {changes?.description && <p className="text-slate-700">{changes.description}</p>}
      {changes?.image_url && <img src={changes.image_url} alt="" className="mt-2 max-h-40 rounded-lg" />}
    </div>
  );
}
